import React, { useState } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

// ============ ANIMATIONS ============
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

// ============ STYLED COMPONENTS ============
const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  animation: ${fadeIn} 200ms ease-out;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  max-width: 420px;
  width: 90%;
  animation: ${slideUp} 300ms ease-out;
`;

const ModalHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 12px;

  .modal-icon {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    color: #f59e0b;
  }

  h2 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: #1e293b;
  }
`;

const ModalBody = styled.div`
  padding: 24px;

  .modal-description {
    font-size: 0.9rem;
    color: #475569;
    line-height: 1.6;
    margin-bottom: 20px;

    strong {
      display: block;
      color: #1e293b;
      margin: 16px 0 8px 0;
    }
  }

  .form-group {
    margin-bottom: 20px;

    label {
      display: block;
      font-size: 0.85rem;
      font-weight: 600;
      color: #334155;
      margin-bottom: 8px;
    }

    textarea {
      width: 100%;
      padding: 10px 12px;
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      font-size: 0.875rem;
      font-family: inherit;
      resize: vertical;
      min-height: 80px;
      transition: all 200ms;

      &:focus {
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }

      &::placeholder {
        color: #94a3b8;
      }
    }

    .char-count {
      font-size: 0.75rem;
      color: #94a3b8;
      margin-top: 4px;
      text-align: right;
    }
  }
`;

const ModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const Button = styled.button`
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 200ms;

  &.primary {
    background: #3b82f6;
    color: white;

    &:hover {
      background: #2563eb;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }

    &:disabled {
      background: #cbd5e1;
      cursor: not-allowed;
    }
  }

  &.secondary {
    background: #e2e8f0;
    color: #334155;

    &:hover {
      background: #cbd5e1;
    }
  }
`;

// ============ ICON COMPONENT ============
const AlertIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3.05h16.94a2 2 0 0 0 1.71-3.05L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

// ============ LICENSE PERMISSION MODAL ============
export const LicensePermissionModal = ({ isOpen, onClose, onSubmit, licenseExpiryDate }) => {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(reason);
      setReason('');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const expiryDate = new Date(licenseExpiryDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <ModalBackdrop onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <div className="modal-icon">
            <AlertIcon />
          </div>
          <h2>License Renewal Request</h2>
        </ModalHeader>

        <ModalBody>
          <div className="modal-description">
            Your professional license is expiring soon.

            <strong>Expiration Date:</strong>
            {expiryDate}

            Please provide information about your renewal status and any relevant details for the admin team to review.
          </div>

          <div className="form-group">
            <label htmlFor="reason">Renewal Reason or Status</label>
            <textarea
              id="reason"
              value={reason}
              onChange={e => setReason(e.target.value.slice(0, 500))}
              placeholder="e.g., 'I have already submitted renewal documents to the licensing board on [date]...' or 'My renewal is in progress...'"
              disabled={isSubmitting}
            />
            <div className="char-count">{reason.length}/500</div>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            className="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            className="primary"
            onClick={handleSubmit}
            disabled={!reason.trim() || isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </ModalBackdrop>
  );
};
